<template>
  <View gap="sm" direction="column">
    <View gap="md" direction="row" justify="space-between" align="center" padding-block="sm">
      <Text variant="h3">Convo summary</Text>
    </View>

    <View v-if="isPending" justify="center" padding-block="lg">
      <LoadingIndicator />
    </View>

    <EmptyState
      v-else-if="allItems.length === 0"
      icon="message-chat-circle"
      color="teal"
      title="No conversations yet"
      description="Conversation summaries will appear here once renters engage."
    />

    <View v-else direction="column" gap="sm">
      <Card
        v-for="item in items"
        :key="item.feedbackId"
        variant="outline"
        size="medium"
        shadow="on-hover"
        class="convo-card"
        role="button"
        tabindex="0"
        @click="openSidebar(item)"
        @keydown.enter="openSidebar(item)"
      >
        <View direction="column" gap="sm">
          <View direction="row" gap="sm" align="center" class="renter">
            <Avatar :user-name="item.renterName || 'Renter'" size="small" />
            <Text variant="label-medium">{{ item.renterName || 'Renter' }}</Text>
          </View>

          <Text variant="body-small" color="secondary" class="clamp-2">
            {{ item.summary }}
          </Text>

          <View
            v-if="convoOutcomeLabel(item.currentOutcome) || item.isActionable"
            direction="row"
            gap="sm"
            align="center"
            wrap="wrap"
          >
            <Badge
              v-if="convoOutcomeLabel(item.currentOutcome)"
              :label="convoOutcomeLabel(item.currentOutcome)!"
              :variant="convoOutcomeVariant(item.currentOutcome)"
              size="small"
            />
            <Badge v-if="item.isActionable" label="Actionable" variant="orange" size="small" />
          </View>
        </View>
      </Card>
    </View>

    <ConversationSummarySidebar
      ref="sidebarRef"
      :property-id="propertyId"
      :item="selectedItem"
      :outcome-label="selectedOutcomeLabel"
      :outcome-variant="selectedOutcomeVariant"
    />
  </View>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import { Avatar, Badge, Card, EmptyState, LoadingIndicator, Text, View } from '@tallo/design-system';
import { ConversationSummarySidebar } from '@tallo/investor/conversation';
import {
  PropertyConvoSummaryDto,
  convoOutcomeLabel,
  convoOutcomeVariant,
  usePropertyConvoSummariesQuery,
} from '@tallo/investor/property';

const props = defineProps<{ limit?: number }>();

const route = useRoute();
const { data, isPending } = usePropertyConvoSummariesQuery();
// Only show conversations that actually have an extracted summary — drop empty ones rather than
// rendering a meaningless "No summary available yet." card (also enforced server-side).
const allItems = computed(() => (data.value || []).filter((item) => item.summary?.trim()));
const items = computed(() => (props.limit ? allItems.value.slice(0, props.limit) : allItems.value));

const propertyId = computed(() => route.params.propertyId as string);

// One shared sidebar instance for the whole list — clicking a card loads that renter's full
// conversation detail. Outcome label/variant are derived here (property-side heuristic) and passed
// down so the panel and the sidebar show the same status.
const sidebarRef = ref<InstanceType<typeof ConversationSummarySidebar>>();
const selectedItem = ref<PropertyConvoSummaryDto | null>(null);
const selectedOutcomeLabel = computed(() => convoOutcomeLabel(selectedItem.value?.currentOutcome));
const selectedOutcomeVariant = computed(() => convoOutcomeVariant(selectedItem.value?.currentOutcome));

function openSidebar(item: PropertyConvoSummaryDto): void {
  selectedItem.value = item;
  sidebarRef.value?.open();
}
</script>

<style scoped lang="scss">
.convo-card {
  cursor: pointer;
}

.convo-card:focus-visible {
  outline: 2px solid var(--t-color-border-primary-hover);
  outline-offset: 2px;
}

.renter {
  min-width: 0;
}

.clamp-2 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  // Reserve two lines so cards keep a consistent height whether the summary is one or two lines.
  min-height: calc(2 * var(--t-line-height-400));
}
</style>
